import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const BuckyballEdges = ({ edges, vertices, isHashing, isSealed }) => {
  const groupRef = useRef();
  const currentSpeed = useRef({ x: 0.5, y: 1 });
  
  // Bright Ceramic White Material (No Emissive Glow to preserve realistic 3D shadows)
  const material = useMemo(() => new THREE.MeshStandardMaterial({
    color: '#ffffff',
    roughness: 0.15,
    metalness: 0.05,
    clearcoat: 0.8,
    clearcoatRoughness: 0.1,
  }), []);

  useFrame((state, delta) => {
    // Smoothly interpolate the rotation speed to avoid jerky movements
    const targetSpeedX = isHashing ? 1.0 : 0.4;
    const targetSpeedY = isHashing ? 1.5 : 0.6;
    
    currentSpeed.current.x = THREE.MathUtils.lerp(currentSpeed.current.x, targetSpeedX, delta * 3);
    currentSpeed.current.y = THREE.MathUtils.lerp(currentSpeed.current.y, targetSpeedY, delta * 3);

    if (groupRef.current) {
      groupRef.current.rotation.x += currentSpeed.current.x * delta;
      groupRef.current.rotation.y += currentSpeed.current.y * delta;
    }
  });

  const edgeMeshes = useMemo(() => {
    if (!vertices || !edges) return null;
    
    return edges.map((edge, index) => {
      const v1 = new THREE.Vector3(...vertices[edge[0]]);
      const v2 = new THREE.Vector3(...vertices[edge[1]]);
      
      const distance = v1.distanceTo(v2);
      const position = v1.clone().lerp(v2, 0.5);
      
      const quaternion = new THREE.Quaternion().setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        v2.clone().sub(v1).normalize()
      );

      return (
        <mesh key={`edge-${index}`} position={position} quaternion={quaternion} material={material}>
          <cylinderGeometry args={[0.04, 0.04, distance + 0.02, 12]} />
        </mesh>
      );
    });
  }, [vertices, edges, material]);

  const vertexMeshes = useMemo(() => {
    if (!vertices) return null;
    return vertices.map((v, index) => (
      <mesh key={`vertex-${index}`} position={v} material={material}>
        <sphereGeometry args={[0.045, 16, 16]} />
      </mesh>
    ));
  }, [vertices, material]);

  // Solid Inner Sphere for the Embossed Look
  const innerSphereMaterial = useMemo(() => new THREE.MeshStandardMaterial({
    color: '#f8fafc', // Extremely light grey/white for the deep pits
    roughness: 0.4,
    metalness: 0.1,
  }), []);

  return (
    <group ref={groupRef}>
      {/* The solid core, slightly smaller than radius 1.0 to create deep hexagonal pits */}
      <mesh material={innerSphereMaterial}>
        <sphereGeometry args={[0.95, 64, 64]} />
      </mesh>
      {edgeMeshes}
      {vertexMeshes}
    </group>
  );
};

const HoneycombSphere = ({ phase }) => {
  const [geometryData, setGeometryData] = useState(null);

  useEffect(() => {
    fetch('/hexasphere.json')
      .then(res => res.json())
      .then(data => setGeometryData(data))
      .catch(err => console.error("Failed to load geometry", err));
  }, []);

  const isHashing = phase === 1;
  const isSealed = phase === 2;

  if (!geometryData) return null;

  return (
    <Canvas camera={{ position: [0, 0, 4.2], fov: 45 }} style={{ width: '100%', height: '100%', background: 'transparent' }}>
      <ambientLight intensity={1.2} />
      <directionalLight position={[10, 20, 15]} intensity={2.5} castShadow />
      <directionalLight position={[-10, -10, -10]} intensity={0.8} />
      <pointLight position={[0, 0, 5]} intensity={1.5} />
      <BuckyballEdges 
        edges={geometryData.edges} 
        vertices={geometryData.vertices} 
        isHashing={isHashing}
        isSealed={isSealed}
      />
    </Canvas>
  );
};

export default HoneycombSphere;
